/**
 * SidebarStep6 - Final Report Generation (Historical BI Path)
 * 
 * This component handles report generation for the Historical BI path.
 * Shows when calculation_mode === 'standard_bi' or default
 * 
 * Features:
 *   - Report generation with status
 *   - BI Calculation Detail display
 *   - Payroll Reconciliation results
 *   - Sensitivity Analysis
 *   - Monte Carlo Analysis
 *   - Data Entry Briefing download
 *   - Report download options (Standard, AI Narrative, Bundle)
 * 
 * Preserves ALL functionality from Step6.tsx
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/TestAuthContext';
import axios from 'axios';
import WizardV2Layout from './WizardV2Layout';
import NarrativeEditor from '../NarrativeEditor';
import { formatCurrency } from '../../../utils/formatCurrency';
import { useActivityLog } from '../../../hooks/useActivityLog';
import { apiFetch } from '../../../utils/api';
import { 
  FileText, 
  Download, 
  CheckCircle, 
  AlertTriangle,
  Loader2,
  BarChart3,
  TrendingUp,
  DollarSign,
  Calculator,
  FileSpreadsheet,
  Sparkles,
  Package,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Info
} from 'lucide-react';

interface SidebarStep6Props {
  goToStep?: (step: number) => void;
  onBack: () => void;
  onComplete: () => void;
  data: any;
  onSave?: (explicitData?: any) => Promise<void>;
  isSaving?: boolean;
  lastSaved?: Date | null;
  highestCompletedStep?: number;
  claimId?: string;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SidebarStep6({ 
  onBack, 
  onComplete,
  goToStep,
  data,
  onSave,
  isSaving,
  lastSaved,
  highestCompletedStep,
  claimId
}: SidebarStep6Props) {
  const [status, setStatus] = useState<'generating' | 'success' | 'error'>('generating');
  const [reportId, setReportId] = useState<string | null>(null);
  const [calculationResults, setCalculationResults] = useState<any>(null);
  const [showNarrator, setShowNarrator] = useState(false);
  const [error, setError] = useState<string>('');
  
  // Sensitivity Analysis State
  const [showSensitivity, setShowSensitivity] = useState(false);
  const [sensitivityParam, setSensitivityParam] = useState('payroll');
  const [sensitivityResults, setSensitivityResults] = useState<any>(null);
  const [sensitivityLoading, setSensitivityLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  
  // Monte Carlo Analysis State
  const [showMonteCarlo, setShowMonteCarlo] = useState(false);
  const [monteCarloResults, setMonteCarloResults] = useState<any>(null);
  const [monteCarloLoading, setMonteCarloLoading] = useState(false);
  
  // Data Entry Briefing State
  const [briefingLoading, setBriefingLoading] = useState(false);
  
  const { getToken } = useAuth();
  const { logActivity } = useActivityLog();
  const navigate = useNavigate();
  const hasGenerated = useRef(false);

  // Handle authenticated PDF download
  const handleDownloadPdf = async () => {
    if (!reportId) return;
    
    try {
      const token = await getToken();
      const blob = await apiFetch<Blob>(`/api/v1/download/report-pdf/${reportId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        showErrorToast: false
      });
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `BI_Report_${reportId.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      logActivity({
        reportId,
        actionType: 'download',
        actionDetails: 'Downloaded Standard Report PDF',
        section: 'step6',
        metadata: { download_type: 'standard_pdf' }
      });
    } catch (error) {
      console.error('PDF download failed:', error);
      setValidationError(`Download failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  useEffect(() => {
    if (!hasGenerated.current) {
      hasGenerated.current = true;
      generateReport();
    }
  }, []);

  const generateReport = async () => {
    try {
      const token = await getToken();
      
      const years = data.calculated_years || [];
      const coverageYear = years.length > 0 ? years[years.length - 1] : (data.coverage_year || new Date().getFullYear());
      
      const payrollData: {[key: string]: number} = {};
      const employeePayrollData: {[key: string]: number} = {};
      const partnerK1Data: {[key: string]: number} = {};
      
      years.forEach((year: number) => {
        if (data[`payroll_${year}`]) {
          payrollData[`payroll_${year}`] = parseFloat(data[`payroll_${year}`]) || 0;
        }
        if (data[`employee_payroll_${year}`]) {
          employeePayrollData[`employee_payroll_${year}`] = parseFloat(data[`employee_payroll_${year}`]) || 0;
        }
        if (data[`partner_k1_income_${year}`]) {
          partnerK1Data[`partner_k1_income_${year}`] = parseFloat(data[`partner_k1_income_${year}`]) || 0;
        }
      });
      
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/reports/generate`,
        {
          client_name: data.client_name,
          claimant_name: data.claimant_name,
          business_address: data.business_address,
          zone: data.zone,
          business_type: data.business_type,
          financial_data: data.financial_data || {},
          plFiles: data.plFiles || [],
          tax1065Files: data.tax1065Files || [],
          uploadedDocuments: data.uploadedDocuments || [],
          tax_data: data.tax_data || [],
          reconciliation: data.reconciliation || [],
          event_type: data.event_type,
          event_start_date: data.event_start_date,
          event_end_date: data.event_end_date,
          calculation_mode: data.calculation_mode || 'standard_bi',
          threshold: parseFloat(data.threshold) || 0,
          threshold_type: data.threshold_type || 'dollar',
          accountant_fees: parseFloat(data.accountant_fees) || 0,
          continuing_expenses: parseFloat(data.continuing_expenses) || 0,
          extra_expenses: parseFloat(data.extra_expenses) || 0,
          adjustment_notes: data.adjustment_notes || '',
          payroll_data: data.payroll_data || payrollData,
          employee_payroll_data: employeePayrollData,
          partner_k1_data: data.partner_k1_data || partnerK1Data,
          calculated_years: years,
          coverage_year: coverageYear,
          ...payrollData,
          ...employeePayrollData,
          ...partnerK1Data
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setReportId(response.data.report_id);
      setCalculationResults(response.data.calculation_results || {});
      
      logActivity({
        reportId: response.data.report_id,
        actionType: 'generate',
        actionDetails: `Generated BI report for ${data.client_name}`,
        section: 'wizard',
        metadata: { 
          calculation_mode: data.calculation_mode || 'standard_bi',
          claim_amount: response.data.calculation_results?.final_claim_amount 
        }
      });
      
      if (response.data.payroll_reconciliation) {
        setCalculationResults({
          ...response.data.calculation_results,
          payroll_reconciliation: response.data.payroll_reconciliation
        });
      }
      
      setStatus('success');
    } catch (err: any) {
      console.error('Report generation failed:', err);
      setError(err.response?.data?.detail || 'Failed to generate report');
      setStatus('error');
    }
  };

  // Download Data Entry Briefing Report as PDF
  const downloadBriefing = async () => {
    if (!reportId) {
      setValidationError('Report must be generated first');
      return;
    }
    
    setBriefingLoading(true);
    try {
      const token = await getToken();
      
      const blob = await apiFetch<Blob>(
        `/api/v1/reports/briefing/${reportId}?format=pdf`,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/pdf'
          },
          showErrorToast: false
        }
      );
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Briefing_Report_${reportId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading briefing:', err);
      setValidationError('Failed to download briefing. Please try again.');
    } finally {
      setBriefingLoading(false);
    }
  };

  // Run Sensitivity Analysis
  const runSensitivityAnalysis = async () => {
    if (!reportId) {
      setValidationError('Report must be generated first');
      return;
    }
    
    setSensitivityLoading(true);
    setSensitivityResults(null);
    
    try {
      const token = await getToken();
      
      const result = await new Promise<any>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BACKEND_URL}/api/v1/reports/${reportId}/sensitivity?parameter=${sensitivityParam}&min_pct=80&max_pct=120&step_pct=5`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              reject(new Error('Failed to parse response'));
            }
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText);
              if (errorData.detail?.error === "CAUSATION_TEST_FAILED") {
                reject(new Error(`⚠️ ${errorData.detail.message}\n\n${errorData.detail.fix}`));
              } else {
                reject(new Error(errorData.detail || `HTTP ${xhr.status}: ${xhr.statusText}`));
              }
            } catch (e) {
              reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
            }
          }
        };
        
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send();
      });
      
      setSensitivityResults(result);
    } catch (err: any) {
      console.error('Sensitivity analysis error:', err);
      setValidationError(`Failed to run sensitivity analysis: ${err.message}`);
    } finally {
      setSensitivityLoading(false);
    }
  };

  // Run Monte Carlo Analysis
  const runMonteCarloAnalysis = async () => {
    if (!reportId) {
      setValidationError('Report must be generated first');
      return;
    }
    
    setMonteCarloLoading(true);
    setMonteCarloResults(null);
    
    try {
      const token = await getToken();
      
      const result = await new Promise<any>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BACKEND_URL}/api/v1/reports/${reportId}/monte-carlo?iterations=1000&noise_pct=0.15&seed=42`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              reject(new Error('Failed to parse response'));
            }
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText);
              if (errorData.detail?.error === "CAUSATION_TEST_FAILED") {
                reject(new Error(`⚠️ ${errorData.detail.message}\n\n${errorData.detail.fix}`));
              } else {
                reject(new Error(errorData.detail || `HTTP ${xhr.status}: ${xhr.statusText}`));
              }
            } catch (e) {
              reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
            }
          }
        };
        
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send();
      });
      
      setMonteCarloResults(result);
    } catch (err: any) {
      console.error('Monte Carlo error:', err);
      setValidationError(`Failed to run Monte Carlo analysis: ${err.message}`);
    } finally {
      setMonteCarloLoading(false);
    }
  };

  // Build sidebar sections
  const buildSections = () => {
    const sections = [
      {
        id: 'status',
        label: 'Report Status',
        isActive: true,
        isComplete: status === 'success',
        onClick: () => document.getElementById('status-section')?.scrollIntoView({ behavior: 'smooth' })
      }
    ];
    
    if (status === 'success') {
      sections.push(
        {
          id: 'calculation',
          label: 'BI Calculation',
          isActive: false,
          isComplete: true,
          onClick: () => document.getElementById('calculation-section')?.scrollIntoView({ behavior: 'smooth' })
        },
        {
          id: 'payroll',
          label: 'Payroll Reconciliation',
          isActive: false,
          isComplete: !!calculationResults?.payroll_reconciliation,
          onClick: () => document.getElementById('payroll-section')?.scrollIntoView({ behavior: 'smooth' })
        },
        {
          id: 'sensitivity',
          label: 'Sensitivity Analysis',
          isActive: false,
          isComplete: !!sensitivityResults,
          onClick: () => document.getElementById('sensitivity-section')?.scrollIntoView({ behavior: 'smooth' })
        },
        {
          id: 'montecarlo',
          label: 'Monte Carlo',
          isActive: false,
          isComplete: !!monteCarloResults,
          onClick: () => document.getElementById('montecarlo-section')?.scrollIntoView({ behavior: 'smooth' })
        },
        {
          id: 'downloads',
          label: 'Download Reports',
          isActive: false,
          isComplete: false,
          onClick: () => document.getElementById('downloads-section')?.scrollIntoView({ behavior: 'smooth' })
        }
      );
    }
    
    return sections;
  };

  // Build QA items
  const buildQAItems = () => {
    const items: Array<{id: string; label: string; status: 'missing' | 'warning' | 'ok'}> = [];
    
    if (status === 'generating') {
      items.push({ id: 'generating', label: 'Generating report...', status: 'warning' });
    } else if (status === 'success') {
      items.push({ id: 'generated', label: 'Report generated', status: 'ok' });
      if (calculationResults?.final_claim_amount) {
        items.push({ id: 'claim', label: `Claim: ${formatCurrency(calculationResults.final_claim_amount)}`, status: 'ok' });
      }
    } else if (status === 'error') {
      items.push({ id: 'error', label: 'Generation failed', status: 'missing' });
    }
    
    return items;
  };

  const completionPercent = status === 'success' ? 100 : status === 'generating' ? 90 : 50;
  const grade = status === 'success' ? 'A' : status === 'generating' ? 'B' : 'D';

  // Show Narrator Editor
  if (showNarrator) {
    return (
      <NarrativeEditor
        claimId={reportId || 'demo'}
        claimData={data}
        calculationResults={calculationResults || {}}
        eventType={data.event_type || 'Hurricane'}
      />
    );
  }

  // Handle Quick Actions from sidebar
  const handleQuickAction = (action: string) => {
    console.log('⚡ Quick Action triggered (Step 6):', action);
    switch (action) {
      case 'rerun':
        // Re-run the analysis - go back to Step 5
        if (goToStep) {
          goToStep(5);
        }
        break;
      case 'preview':
        // Scroll to report preview section
        const reportSection = document.getElementById('report-section');
        if (reportSection) {
          reportSection.scrollIntoView({ behavior: 'smooth' });
        }
        break;
      default:
        console.log('Unknown quick action:', action);
    }
  };

  return (
    <WizardV2Layout
      currentStep={6}
      highestCompletedStep={highestCompletedStep}
      stepTitle="Final Report"
      stepSubtitle="Historical BI Claim Report"
      completionPercent={completionPercent}
      calculationMode={data.calculation_mode || 'standard_bi'}
      grade={grade}
      sections={buildSections()}
      qaItems={buildQAItems()}
      isSaving={isSaving}
      lastSaved={lastSaved}
      onSave={onSave}
      goToStep={goToStep}
      onBack={onBack}
      showNext={false}
      claimId={claimId}
      onQuickAction={handleQuickAction}
    >
      <div className="p-6 lg:p-8 space-y-6">

        {validationError && (
          <p className="text-red-500 text-sm">{validationError}</p>
        )}

        {/* Generating Status */}
        {status === 'generating' && (
          <div id="status-section" className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Generating your BI report...</h2>
            <p className="text-gray-500">This may take a few moments</p>
          </div>
        )}

        {/* Error Status */}
        {status === 'error' && (
          <div id="status-section" className="bg-white rounded-xl border border-red-200 p-12 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-red-600 mb-2">Generation Failed</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            
            <div className="flex gap-4 justify-center">
              <button
                onClick={onBack}
                className="px-6 py-3 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl font-semibold transition-colors"
              >
                Back
              </button>
              <button
                onClick={() => {
                  hasGenerated.current = false;
                  setStatus('generating');
                  generateReport();
                }}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Success Status */}
        {status === 'success' && (
          <>
            {/* Success Banner */}
            <div id="status-section" className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Report Generated Successfully!</h2>
                  <p className="text-green-100">Report ID: {reportId}</p>
                </div>
              </div>
            </div>

            {/* Final Claim Amount */}
            {calculationResults?.final_claim_amount && (
              <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl p-8 text-white text-center">
                <p className="text-blue-200 text-sm font-medium mb-2">FINAL CLAIM AMOUNT</p>
                <p className="text-5xl font-bold">
                  {formatCurrency(calculationResults.final_claim_amount)}
                </p>
              </div>
            )}

            {/* BI Calculation Detail */}
            {calculationResults?.calculation_detail && (
              <div id="calculation-section" className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Calculator className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">BI Calculation Detail</h3>
                </div>
                
                {(() => {
                  const detail = calculationResults.calculation_detail;
                  const bp = detail.benchmark_period;
                  const cp = detail.coverage_period;
                  const vp = detail.variable_profit_analysis;
                  const ga = detail.growth_adjustment;
                  const ae = detail.additional_expenses;
                  const fc = detail.final_calculation;
                  
                  return (
                    <div className="space-y-4">
                      {/* Benchmark Period */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-bold text-blue-900 mb-2">🎯 Benchmark Period Selection</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600">Period:</p>
                            <p className="font-bold text-blue-800">{bp.start} - {bp.end}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Type:</p>
                            <p className="font-bold text-blue-800">{bp.type}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Benchmark Revenue:</p>
                            <p className="font-bold text-blue-800">{formatCurrency(bp.revenue)}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Benchmark VP:</p>
                            <p className="font-bold text-blue-800">{formatCurrency(bp.variable_profit)}</p>
                          </div>
                        </div>
                        {bp.why_selected && (
                          <div className="mt-3 p-2 bg-blue-100 rounded text-xs text-blue-800">
                            <strong>Why selected:</strong> {bp.why_selected}
                          </div>
                        )}
                      </div>
                      
                      {/* Coverage Period */}
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <h4 className="font-bold text-orange-900 mb-2">📉 Coverage Period (Event Year)</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600">Period:</p>
                            <p className="font-bold text-orange-800">{cp.start} - {cp.end}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Coverage VP:</p>
                            <p className="font-bold text-orange-800">{formatCurrency(cp.variable_profit)}</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Variable Profit Loss */}
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <h4 className="font-bold text-red-900 mb-2">💰 Step 1: Variable Profit Loss</h4>
                        <div className="font-mono text-sm space-y-1">
                          <div className="flex justify-between">
                            <span>Benchmark VP:</span>
                            <span className="font-bold">{formatCurrency(vp.benchmark_vp)}</span>
                          </div>
                          <div className="flex justify-between text-red-700">
                            <span>Coverage VP:</span>
                            <span className="font-bold">-{formatCurrency(vp.coverage_vp)}</span>
                          </div>
                          <div className="border-t border-red-300 pt-1 flex justify-between font-bold text-red-900">
                            <span>VP Loss:</span>
                            <span>{formatCurrency(vp.vp_loss)}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Growth Adjustment */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-bold text-blue-900 mb-2">📈 Step 2: Growth/Trend Adjustment</h4>
                        <div className="font-mono text-sm space-y-1">
                          <div className="flex justify-between">
                            <span>Claimant Factor:</span>
                            <span className="font-bold">{ga.claimant_factor_pct?.toFixed(1)}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span>General Factor:</span>
                            <span className="font-bold">{ga.general_factor_pct?.toFixed(1)}%</span>
                          </div>
                          <div className="border-t border-blue-300 pt-1 flex justify-between font-bold text-blue-900">
                            <span>Incremental Lost Profit:</span>
                            <span>{formatCurrency(ga.incremental_lost_profit)}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Additional Expenses */}
                      {ae?.total_additional > 0 && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <h4 className="font-bold text-yellow-900 mb-2">➕ Additional Expenses</h4>
                          <div className="font-mono text-sm space-y-1">
                            <div className="flex justify-between">
                              <span>Accountant Fees:</span>
                              <span className="font-bold">{formatCurrency(ae.accountant_fees)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Other Adjustments:</span>
                              <span className="font-bold">{formatCurrency(ae.other_adjustments)}</span>
                            </div>
                            <div className="border-t border-yellow-300 pt-1 flex justify-between font-bold text-yellow-900">
                              <span>Total Additional:</span>
                              <span>{formatCurrency(ae.total_additional)}</span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Final Calculation */}
                      <div className="bg-green-50 border-2 border-green-400 rounded-lg p-4">
                        <h4 className="font-bold text-green-900 mb-2">🎯 Final Calculation</h4>
                        <div className="font-mono text-sm space-y-1">
                          <div className="flex justify-between">
                            <span>Step 1 (VP Loss):</span>
                            <span className="font-bold">{formatCurrency(fc.step1_vp_loss)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Step 2 (Incremental):</span>
                            <span className="font-bold">{formatCurrency(fc.step2_incremental)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Additional Expenses:</span>
                            <span className="font-bold">{formatCurrency(fc.additional_expenses)}</span>
                          </div>
                          <div className="border-t-2 border-green-500 pt-2 mt-2 flex justify-between text-lg font-bold text-green-900">
                            <span>TOTAL COMPENSABLE LOSS:</span>
                            <span>{formatCurrency(fc.final_claim)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Payroll Reconciliation */}
            {calculationResults?.payroll_reconciliation && (
              <div id="payroll-section" className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Payroll Reconciliation</h3>
                </div>
                
                {(() => {
                  const recon = calculationResults.payroll_reconciliation;
                  const k1Detected = recon.k1_properly_separated || recon.flags?.includes('K1_PROPERLY_SEPARATED');
                  
                  return (
                    <div className={`p-4 rounded-lg ${k1Detected ? 'bg-blue-50 border border-blue-200' : 'bg-green-50 border border-green-200'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xl">{k1Detected ? 'ℹ️' : '✅'}</span>
                        <span className="font-semibold text-gray-900">
                          {k1Detected ? 'K-1 Income Detected' : 'Employee Payroll Verified'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        {k1Detected 
                          ? 'Partner K-1 income has been properly separated from employee payroll for accurate VP calculations.'
                          : `Payroll variance: ${recon.variance_pct || 0}%`}
                      </p>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Sensitivity Analysis */}
            <div id="sensitivity-section" className="bg-white rounded-xl border-2 border-indigo-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Sensitivity Analysis</h3>
                </div>
                <button
                  onClick={() => setShowSensitivity(!showSensitivity)}
                  className="text-indigo-600 hover:text-indigo-800 text-sm font-semibold flex items-center gap-1"
                >
                  {showSensitivity ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  {showSensitivity ? 'Hide' : 'Show'}
                </button>
              </div>
              
              {showSensitivity && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Test how your claim amount changes when key parameters vary ±20%.
                  </p>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Test Parameter:
                    </label>
                    <select
                      value={sensitivityParam}
                      onChange={(e) => setSensitivityParam(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600"
                    >
                      <option value="payroll">Employee Payroll (±20%)</option>
                      <option value="revenue">Revenue (±20%)</option>
                      <option value="variable_expenses">Variable Expenses (±20%)</option>
                    </select>
                  </div>
                  
                  <button
                    onClick={runSensitivityAnalysis}
                    disabled={sensitivityLoading}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded-lg transition disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {sensitivityLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Running Analysis...
                      </>
                    ) : (
                      'Run Sensitivity Analysis'
                    )}
                  </button>
                  
                  {sensitivityResults && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
                      <h4 className="font-bold text-gray-900 mb-3">Results:</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-4 py-2 text-left">Parameter %</th>
                              <th className="px-4 py-2 text-right">Claim Amount</th>
                              <th className="px-4 py-2 text-right">Change</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sensitivityResults.results.map((row: any, idx: number) => (
                              <tr key={idx} className={`border-t ${row.pct === 100 ? 'bg-indigo-50 font-bold' : ''}`}>
                                <td className="px-4 py-2">{row.pct}%{row.pct === 100 ? ' (Base)' : ''}</td>
                                <td className="px-4 py-2 text-right">{formatCurrency(row.claim)}</td>
                                <td className={`px-4 py-2 text-right ${row.change_pct > 0 ? 'text-green-600' : row.change_pct < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                  {row.change_pct > 0 ? '+' : ''}{row.change_pct}%
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      <div className="mt-4 p-3 bg-indigo-50 border border-indigo-200 rounded">
                        <p className="text-sm text-indigo-900">
                          <strong>Range:</strong> {formatCurrency(sensitivityResults.range.min)} - {formatCurrency(sensitivityResults.range.max)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Monte Carlo Analysis */}
            <div id="montecarlo-section" className="bg-white rounded-xl border-2 border-green-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Monte Carlo Analysis</h3>
                    <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs font-bold">FORENSIC-GRADE</span>
                  </div>
                </div>
                <button
                  onClick={() => setShowMonteCarlo(!showMonteCarlo)}
                  className="text-green-600 hover:text-green-800 text-sm font-semibold flex items-center gap-1"
                >
                  {showMonteCarlo ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  {showMonteCarlo ? 'Hide' : 'Show'}
                </button>
              </div>
              
              {showMonteCarlo && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Run 1,000+ simulations with randomized revenue variations (±15% Gaussian noise) 
                    to produce 95% confidence intervals.
                  </p>
                  
                  <button
                    onClick={runMonteCarloAnalysis}
                    disabled={monteCarloLoading}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-lg transition disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {monteCarloLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Running 1,000 Simulations...
                      </>
                    ) : (
                      'Run Monte Carlo Analysis'
                    )}
                  </button>
                  
                  {monteCarloResults && (
                    <div className="bg-gradient-to-br from-green-50 to-blue-50 border-2 border-green-300 rounded-lg p-6 mt-4">
                      <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        Monte Carlo Complete
                        <span className="text-xs text-gray-500">
                          ({monteCarloResults.iterations.toLocaleString()} iterations)
                        </span>
                      </h4>
                      
                      <div className="bg-white rounded-lg p-4 mb-4">
                        <p className="text-sm text-gray-600 mb-1">Base Claim (Deterministic):</p>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(monteCarloResults.base_claim)}</p>
                      </div>
                      
                      <div className="bg-gradient-to-r from-green-100 to-blue-100 rounded-lg p-4 mb-4 border-2 border-green-400">
                        <p className="text-sm font-bold text-green-900 mb-2">95% Confidence Interval:</p>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-600">Low (2.5th)</p>
                            <p className="text-lg font-bold text-green-800">{formatCurrency(monteCarloResults.results.ci_95_low)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-600">Median</p>
                            <p className="text-lg font-bold text-gray-900">{formatCurrency(monteCarloResults.results.median)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-600">High (97.5th)</p>
                            <p className="text-lg font-bold text-green-800">{formatCurrency(monteCarloResults.results.ci_95_high)}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-blue-50 border-l-4 border-blue-500 p-3">
                        <p className="text-sm text-blue-900">
                          <strong>Interpretation:</strong> With 95% confidence, the true claim amount 
                          lies between <strong>{formatCurrency(monteCarloResults.results.ci_95_low)}</strong> and{' '}
                          <strong>{formatCurrency(monteCarloResults.results.ci_95_high)}</strong>.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Data Entry Briefing */}
            <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-amber-800 flex items-center gap-2">
                    <FileSpreadsheet className="w-5 h-5" />
                    Data Entry Briefing
                  </h3>
                  <p className="text-sm text-amber-700 mt-1">
                    Quick 3-5 page handoff document with gaps, flags, and action items.
                  </p>
                </div>
                <button
                  onClick={downloadBriefing}
                  disabled={briefingLoading}
                  className={`px-6 py-2 rounded-lg font-semibold transition flex items-center gap-2 ${
                    briefingLoading 
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-amber-500 hover:bg-amber-600 text-white'
                  }`}
                >
                  {briefingLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Download Briefing
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Download Options */}
            <div id="downloads-section" className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Download Reports</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Standard Report */}
                <div className="bg-white border-2 border-green-300 rounded-xl p-6 hover:shadow-lg transition">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-gray-900">Standard Report</h3>
                    <span className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-bold">FREE</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Complete BI calculation report (11 pages) with all formulas and methodology.
                  </p>
                  <ul className="text-sm text-gray-700 mb-4 space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                      <span>Complete BI breakdown</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                      <span>Causation test results</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                      <span>Financial data tables</span>
                    </li>
                  </ul>
                  <button
                    onClick={handleDownloadPdf}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-lg transition flex items-center justify-center gap-2"
                  >
                    <FileText className="w-5 h-5" />
                    Download (11 pages)
                  </button>
                </div>

                {/* AI Narrative */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-50 border-2 border-blue-300 rounded-xl p-6 hover:shadow-lg transition">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-blue-900">AI Narrative</h3>
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-bold">$150</span>
                  </div>
                  <p className="text-sm text-gray-700 mb-4">
                    14-page court-ready legal narrative with professional language.
                  </p>
                  <ul className="text-sm text-gray-700 mb-4 space-y-2">
                    <li className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 text-blue-600 mt-0.5" />
                      <span>AI-powered legal writing</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 text-blue-600 mt-0.5" />
                      <span>Expert testimony format</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 text-blue-600 mt-0.5" />
                      <span>60 seconds vs. 4 hours</span>
                    </li>
                  </ul>
                  <button
                    onClick={() => setShowNarrator(true)}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-600 hover:from-blue-700 hover:to-blue-700 text-white font-bold py-3 rounded-lg transition shadow-lg flex items-center justify-center gap-2"
                  >
                    <Sparkles className="w-5 h-5" />
                    Generate AI Report
                  </button>
                </div>

                {/* Complete Bundle */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-6 hover:shadow-lg transition relative">
                  <div className="absolute -top-3 -right-3 bg-red-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                    SAVE $50
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-blue-900">Complete Bundle</h3>
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-bold">$200</span>
                  </div>
                  <p className="text-sm text-gray-700 mb-4">
                    Everything: Standard + AI Narrative + Premium analytics (25+ pages).
                  </p>
                  <ul className="text-sm text-gray-700 mb-4 space-y-2">
                    <li className="flex items-start gap-2">
                      <Package className="w-4 h-4 text-blue-600 mt-0.5" />
                      <span>Standard Standard BI Report</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Package className="w-4 h-4 text-blue-600 mt-0.5" />
                      <span>AI Legal Narrative</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Package className="w-4 h-4 text-blue-600 mt-0.5" />
                      <span>Sensitivity + Monte Carlo</span>
                    </li>
                  </ul>
                  <button
                    onClick={() => setValidationError('Bundle generation coming soon!')}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-3 rounded-lg transition shadow-lg flex items-center justify-center gap-2"
                  >
                    <Package className="w-5 h-5" />
                    Get Bundle (25 pages)
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 justify-center pt-4">
              <button
                onClick={() => goToStep?.(7)}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold py-3 px-8 rounded-lg shadow-lg transition-all flex items-center gap-2"
              >
                Continue to Final Review
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <button
                onClick={() => navigate('/')}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-3 px-6 rounded-lg transition-colors"
              >
                Save & Exit
              </button>
            </div>
          </>
        )}

      </div>
    </WizardV2Layout>
  );
}
